import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import {
  ArrowRight, Zap, Layout, Monitor, Menu, X,
  CheckCircle, Star, Terminal, Code, Smartphone, Palette, Eye,
  MessageSquare, Briefcase, Coffee, Rocket, Globe,
  BarChart3, Layers, Send, Trophy,
  Gamepad2, Users, ShoppingBag, Film, Glasses, Radio,
  Tent, Share2, Cpu, Video, Megaphone, MapPin, Music, Calendar
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ThankYouPopup from './components/ThankYouPopup';
import AdminDashboard from './components/AdminDashboard';

const API_URL = '/.netlify/functions';

const LandingPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const scrollContainerRef = useRef(null);

  // Visitor & Analytics Tracking
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Generate or retrieve visitor ID (persists across sessions)
    let visitorId = localStorage.getItem('nxto_vid');
    if (!visitorId) {
      visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      localStorage.setItem('nxto_vid', visitorId);
    }

    // Generate or retrieve session ID (new per tab/session)
    let sessionId = sessionStorage.getItem('nxto_sid');
    if (!sessionId) {
      sessionId = 's_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      sessionStorage.setItem('nxto_sid', sessionId);
    }

    // Capture UTM parameters and referrer
    const params = new URLSearchParams(window.location.search);
    const trackingData = {
      visitorId,
      sessionId,
      page: window.location.pathname,
      referrer: document.referrer || '',
      utmSource: params.get('utm_source') || '',
      utmMedium: params.get('utm_medium') || '',
      utmCampaign: params.get('utm_campaign') || ''
    };

    // Track page view
    fetch(`${ API_URL }/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(trackingData)
    }).catch(err => console.error('Tracking failed', err));

    // Track session duration on page unload
    const pageLoadTime = Date.now();
    const handleUnload = () => {
      const duration = Math.round((Date.now() - pageLoadTime) / 1000);
      const beaconData = JSON.stringify({
        update: true,
        visitorId,
        sessionId,
        page: window.location.pathname,
        duration
      });
      navigator.sendBeacon(`${ API_URL }/track`, beaconData);
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const res = await fetch(`${ API_URL }/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        setShowThankYou(true);
        e.target.reset();
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (err) {
      alert('Network error. Please try again.');
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollBy({ left: -350, behavior: 'smooth' });
  };
  const scrollRight = () => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollBy({ left: 350, behavior: 'smooth' });
  };

  // --- STYLING CONSTANTS ---
  const hardShadow = "border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200";
  const cardBase = "bg-white border-2 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] rounded-xl";
  const buttonBase = "font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all active:translate-y-1 active:shadow-none";

  // --- ANIMATION VARIANTS ---
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 260, damping: 20 } }
  };

  const popHover = {
    scale: 1.05,
    rotate: [0, -1, 1, -1, 0],
    transition: { duration: 0.2 }
  };

  // --- DATA SOURCES ---
  const clients = [
    { name: "Wow Momo", src: "/Wow_Momo_Logo.jpg" },
    { name: "Athena", src: "/Athena.avif" },
    { name: "Apollo", src: "/1621861358306.jpeg" },
    { name: "KFC", src: "/KFC logo.png" },
    { name: "Loco", src: "/images (3).png" },
    { name: "Eattos", src: "/eattos.png" },
    { name: "Atomberg", src: "/images.png" },
    { name: "MSI", src: "/msi.png" },
  ];

  const eventServices = [
    { title: "Corporate Events", desc: "Conferences, dealer meets, & leadership summits.", icon: <Briefcase /> },
    { title: "Virtual / Hybrid", desc: "Live-streamed studios & multi-camera productions.", icon: <Monitor /> },
    { title: "Brand Launches", desc: "Immersive setups to create buzz & excitement.", icon: <Rocket /> },
    { title: "Festivals", desc: "Large cultural & youth festivals with high engagement.", icon: <Tent /> },
    { title: "Award Shows", desc: "Premium staging, celebrity handling & execution.", icon: <Trophy /> },
    { title: "Concerts", desc: "High-energy live music & world-class production.", icon: <Music /> },
    { title: "Market Entry", desc: "Strategies for Tier 1-3 markets with cultural depth.", icon: <Globe /> },
    { title: "Gaming / Esports", desc: "Tournaments & experiential setups for gamers.", icon: <Gamepad2 /> },
    { title: "Pop Culture", desc: "Fandom events, cosplay zones & youth activations.", icon: <Star /> },
  ];

  const activationServices = [
    { title: "Brand Activations", desc: "Interactive demos & sampling.", icon: <Zap />, color: "bg-[#FF6B6B]" },
    { title: "Retail Branding", desc: "Visual merchandising & store makeovers.", icon: <ShoppingBag />, color: "bg-[#FFD233]" },
    { title: "Institutional", desc: "School, college & CSR outreach.", icon: <Users />, color: "bg-[#A0E7E5]" },
    { title: "Pop-Up Stores", desc: "Short-term buzz installations.", icon: <Eye />, color: "bg-[#8C52FF]" },
    { title: "Community", desc: "Meet-ups for loyal fans.", icon: <Coffee />, color: "bg-[#FF6B6B]" },
    { title: "Influencers", desc: "On-ground creator integrations.", icon: <Smartphone />, color: "bg-[#FFD233]" },
    { title: "Campus Fests", desc: "Youth-driven college events.", icon: <Code />, color: "bg-[#A0E7E5]" },
    { title: "Mall / Store", desc: "High-footfall engagement.", icon: <MapPin />, color: "bg-[#8C52FF]" },
  ];

  const commsServices = [
    { title: "Brand Comms", desc: "Strategic messaging & storytelling.", icon: <MessageSquare /> },
    { title: "Social & Digital", desc: "Amplification & event coverage.", icon: <Share2 /> },
    { title: "Motion Graphics", desc: "Event visuals & animations.", icon: <Film /> },
    { title: "Tech Marketing", desc: "Demos for innovation brands.", icon: <Cpu /> },
    { title: "PR & Media", desc: "Press conferences & visibility.", icon: <Radio /> },
    { title: "Ad Films", desc: "High-quality campaign videos.", icon: <Video /> },
    { title: "AR / VR", desc: "Immersive futuristic experiences.", icon: <Glasses /> },
  ];

  return (
    <div className="min-h-screen bg-[#F0F4F8] text-black font-sans overflow-x-hidden selection:bg-[#FF6B6B] selection:text-white">
      <ThankYouPopup isOpen={showThankYou} onClose={() => setShowThankYou(false)} />

      {/* Decorative Background Grid */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.05]"
        style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
      </div>

      {/* Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100 }}
        className={`fixed w-full z-50 transition-all duration-300 ${ scrollY > 20 ? 'py-3 bg-[#F0F4F8]/90 backdrop-blur-md border-b-2 border-black' : 'py-6 bg-transparent' }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer group" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <motion.img
              whileHover={{ rotate: -12 }}
              src="/logo.png" alt="NXTO Logo" className="h-12 object-contain transition-transform" />
          </div>

          <div className="hidden md:flex items-center gap-6">
            {['Services', 'Events', 'Activations', 'Work', 'Contact'].map((item) => (
              <button key={item} onClick={() => document.getElementById(item.toLowerCase().split(' ')[0])?.scrollIntoView({ behavior: 'smooth' })}
                className="font-bold text-sm uppercase tracking-wide hover:text-[#FF6B6B] hover:underline decoration-wavy underline-offset-4 transition-all">
                {item}
              </button>
            ))}
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "2px 2px 0px 0px rgba(0,0,0,1)" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className={`bg-[#FF6B6B] text-white px-6 py-2.5 rounded-lg text-sm ${ buttonBase } ${ hardShadow }`}>
              Start Project
            </motion.button>
          </div>

          <button className="md:hidden p-2 border-2 border-black rounded bg-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-y-1 active:shadow-none transition-all" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="absolute top-full left-0 w-full bg-white border-b-2 border-black p-6 flex flex-col gap-4 md:hidden shadow-xl"
            >
              {['Services', 'Events', 'Activations', 'Work', 'Contact'].map((item, i) => (
                <motion.button
                  key={item}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => {
                    document.getElementById(item.toLowerCase().split(' ')[0])?.scrollIntoView({ behavior: 'smooth' });
                    setIsMenuOpen(false);
                  }} className="text-left font-bold text-xl py-3 border-b-2 border-dashed border-gray-200 hover:text-[#8C52FF]">{item}</motion.button>
              ))}
              <motion.button
                whileTap={{ scale: 0.95 }}
                className={`bg-[#FF6B6B] text-white w-full py-4 rounded-lg text-lg border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] ${ buttonBase }`}>
                Start Project
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-16 px-6 overflow-hidden">
        <motion.div
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-32 left-[5%] hidden md:block"
        >
          <div className="bg-[#FFD233] p-3 rounded-none border-2 border-black shadow-[4px_4px_0px_0px_black] rotate-[-6deg]">
            <Code size={24} className="text-black" />
          </div>
        </motion.div>
        <motion.div
          animate={{ y: [0, 20, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-20 right-[10%] hidden md:block"
        >
          <div className="bg-[#A0E7E5] p-3 rounded-full border-2 border-black shadow-[4px_4px_0px_0px_black] rotate-[12deg]">
            <BarChart3 size={24} className="text-black" />
          </div>
        </motion.div>

        <div className="max-w-5xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            className="inline-block bg-white border-2 border-black px-4 py-1.5 rounded-lg mb-6 transform rotate-1 hover:rotate-0 transition-transform cursor-default shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
          >
            <span className="font-bold text-xs md:text-sm tracking-wide">🚀 Development + Strategy = Growth</span>
          </motion.div>

          <h1 className="text-4xl md:text-7xl font-black text-black tracking-tighter leading-snug md:leading-[1.1] mb-10 uppercase">
            <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>ELEVATE YOUR</motion.span> <br />
            <motion.span
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4, type: "spring" }}
              className="relative inline-block text-white px-3 mx-1 md:px-4 md:mx-2"
            >
              <span className="absolute inset-0 bg-black transform -skew-x-6 translate-y-1 md:translate-y-2"></span>
              <span className="absolute inset-0 bg-[#FF6B6B] border-2 border-black transform -skew-x-6"></span>
              <span className="relative z-10">BRAND</span>
            </motion.span>
            <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>WITH</motion.span> <br />
            <motion.span
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
              className="inline-block text-[#8C52FF] underline decoration-wavy decoration-2 md:decoration-4 underline-offset-4 md:underline-offset-8"
            >
              NEXT GEN SOLUTIONS.
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="text-lg md:text-xl font-medium text-gray-700 max-w-3xl mx-auto mb-10 leading-relaxed"
          >
            From innovative <span className="font-bold bg-[#A0E7E5] px-1 border border-black">development</span> to strategic <span className="font-bold bg-[#FFD233] px-1 border border-black">marketing</span>, we craft experiences that actually move the needle. No fluff, just results.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <motion.button
              whileHover={{ scale: 1.05, x: 5 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className={`bg-[#FFD233] text-black px-8 py-4 rounded-lg text-lg ${ buttonBase } ${ hardShadow } group`}>
              Let's Scale Your Biz
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 rounded-lg text-lg font-bold border-2 border-black bg-white hover:bg-gray-50 transition-colors shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
              Explore Services
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Marquee Section */}
      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        whileInView={{ opacity: 1, scaleX: 1 }}
        viewport={{ once: true }}
        className="bg-black text-white border-y-2 border-black py-3 overflow-hidden transform rotate-1 relative z-20 shadow-lg"
      >
        <motion.div
          animate={{ x: [0, -1000] }}
          transition={{ ease: "linear", duration: 20, repeat: Infinity }}
          className="flex whitespace-nowrap gap-12"
        >
          {[...Array(10)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 font-black text-lg uppercase tracking-widest">
              <Zap className="fill-[#FFD233] text-[#FFD233] w-5 h-5" />
              <span>Digital Transformation</span>
              <Star className="fill-[#FF6B6B] text-[#FF6B6B] w-5 h-5" />
              <span>Brand Strategy</span>
              <Globe className="text-[#A0E7E5] w-5 h-5" />
              <span>Global Reach</span>
              <Rocket className="text-[#8C52FF] w-5 h-5" />
              <span>Exclusive Experiences</span>
              <BarChart3 className="text-[#FFD233] w-5 h-5" />
              <span>Performance Marketing</span>
              <Smartphone className="text-[#FF6B6B] w-5 h-5" />
              <span>Growth</span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* CLIENTS SECTION */}
      <section className="py-20 px-6 bg-white relative z-10 border-b-2 border-black">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter mb-4">
              <span className="bg-[#A0E7E5] px-2 text-black transform rotate-2 inline-block shadow-[4px_4px_0px_0px_black] border-2 border-black">Trusted</span> By
            </h2>
            <p className="text-lg font-bold text-gray-500 uppercase tracking-widest">Brands we have worked with</p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 md:gap-6 items-center place-items-center"
          >
            {clients.map((client, idx) => (
              <motion.div
                key={idx}
                variants={scaleIn}
                whileHover={{ scale: 1.1, rotate: idx % 2 === 0 ? 3 : -3 }}
                className="w-full max-w-[140px] aspect-square bg-white border-2 border-black rounded-xl p-2 md:p-4 flex items-center justify-center shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_#FF6B6B] transition-all bg-white"
              >
                <img
                  src={client.src}
                  alt={client.name}
                  className="w-full h-full object-contain"
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section id="services" className="py-24 px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6"
          >
            <div>
              <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tighter">DIGITAL EXPERTISE</h2>
              <p className="text-xl font-medium text-gray-600 max-w-md">The foundation of modern business.</p>
            </div>
            <motion.div
              whileHover={{ rotate: 5, scale: 1.1 }}
              className="bg-[#8C52FF] text-white px-6 py-3 rounded-lg font-bold border-2 border-black transform -rotate-1 shadow-[4px_4px_0px_0px_black]"
            >
              🔥 Full-Service Agency
            </motion.div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {[
              { t: "Web Development", i: <Layout />, c: "bg-[#FF6B6B]", d: "Fast, responsive, and built on rock-solid code (React, Next.js)." },
              { t: "App Development", i: <Smartphone />, c: "bg-[#FFD233]", d: "Native and cross-platform mobile apps that users actually enjoy." },
              { t: "Software Development", i: <Terminal />, c: "bg-[#A0E7E5]", d: "Robust software tailored to streamline your specific operations." },
              { t: "Digital Marketing", i: <Rocket />, c: "bg-[#8C52FF]", d: "Data-driven campaigns that maximize ROI and brand visibility." },
              { t: "Brand Strategy", i: <Palette />, c: "bg-black text-white", d: "Visual identities and narratives that stick in people's brains." },
              { t: "Consulting", i: <Briefcase />, c: "bg-[#F0F4F8]", d: "Expert guidance on tech stacks, digital transformation, and scaling." }
            ].map((s, idx) => (
              <motion.div
                key={idx}
                variants={scaleIn}
                whileHover={popHover}
                className={`bg-white ${ cardBase } p-8 flex flex-col group cursor-pointer`}
              >
                <div className={`w-14 h-14 ${ s.c } border-2 border-black rounded-lg flex items-center justify-center mb-6 shadow-[4px_4px_0px_0px_black] ${ s.c.includes('text-white') ? 'text-white' : 'text-black' }`}>
                  {React.cloneElement(s.i, { size: 32 })}
                </div>
                <h3 className="text-2xl font-black mb-3">{s.t}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{s.d}</p>
                <div className="mt-auto flex items-center gap-2 font-bold text-sm hover:underline decoration-wavy">More Info <ArrowRight size={16} /></div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* EVENTS (Horizontal Slider) */}
      <section id="events" className="py-24 border-y-2 border-black bg-[#FFD233] relative">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6"
          >
            <div>
              <div className="inline-block bg-black text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4">
                ✨ Offline Experiences
              </div>
              <h2 className="text-5xl md:text-7xl font-black leading-[0.9] text-black tracking-tighter">
                THE MAIN STAGE
              </h2>
            </div>
            <div className="flex gap-4">
              <motion.button whileTap={{ scale: 0.9 }} onClick={scrollLeft} className="w-14 h-14 bg-white border-2 border-black rounded-full flex items-center justify-center shadow-[4px_4px_0px_0px_black] hover:shadow-none hover:translate-y-1 transition-all"><ArrowRight className="rotate-180" /></motion.button>
              <motion.button whileTap={{ scale: 0.9 }} onClick={scrollRight} className="w-14 h-14 bg-white border-2 border-black rounded-full flex items-center justify-center shadow-[4px_4px_0px_0px_black] hover:shadow-none hover:translate-y-1 transition-all"><ArrowRight /></motion.button>
            </div>
          </motion.div>

          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto gap-8 pb-12 snap-x snap-mandatory hide-scrollbar"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {eventServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true, margin: "-50px" }}
                whileHover={{ rotate: -1, y: -10 }}
                className="snap-center shrink-0 w-[85vw] sm:w-[300px] md:w-[380px] bg-white border-2 border-black rounded-2xl flex flex-col overflow-hidden shadow-[8px_8px_0px_0px_black]"
              >
                <div className="h-32 bg-[#A0E7E5] border-b-2 border-black flex items-center justify-center relative overflow-hidden group">
                  <div className="absolute inset-0 bg-black/10 translate-y-full group-hover:translate-y-0 transition-transform"></div>
                  {React.cloneElement(service.icon, { size: 48, className: "text-black relative z-10" })}
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <h3 className="text-2xl font-black uppercase mb-4 leading-none">{service.title}</h3>
                  <p className="text-gray-600 font-medium mb-6 leading-relaxed">
                    {service.desc}
                  </p>
                  <div className="mt-auto pt-4 flex justify-between items-center">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                      className="text-xs font-black uppercase bg-black text-white px-4 py-2 rounded-lg hover:bg-[#FF6B6B] transition-colors border-2 border-transparent hover:border-black"
                    >
                      Book This
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
            <div className="w-4 shrink-0"></div>
          </div>
        </div>
      </section>

      {/* ACTIVATIONS (Sticker Grid) */}
      <section id="activations" className="py-24 px-6 bg-white relative">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-4 uppercase tracking-tighter">
              <span className="bg-[#FF6B6B] px-2 text-white transform -skew-x-6 inline-block mr-3 border-2 border-black shadow-[4px_4px_0px_0px_black]">On The</span>
              Ground
            </h2>
            <p className="text-xl font-bold text-gray-500">Brand Awareness & Activations</p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {activationServices.map((item, i) => (
              <motion.div
                key={i}
                variants={scaleIn}
                whileHover={{ scale: 1.05, rotate: i % 2 === 0 ? 2 : -2 }}
                className="group"
              >
                <div className={`h-full border-2 border-black rounded-xl p-6 flex flex-col items-start gap-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.15)] group-hover:shadow-[6px_6px_0px_0px_black] transition-all bg-white hover:bg-gray-50`}>
                  <div className={`p-3 rounded-lg border-2 border-black ${ item.color } text-black shadow-[2px_2px_0px_0px_black]`}>
                    {React.cloneElement(item.icon, { size: 24 })}
                  </div>
                  <div>
                    <h3 className="font-black text-xl uppercase leading-none mb-2">{item.title}</h3>
                    <p className="text-sm font-bold text-gray-500 leading-snug">{item.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* COMMUNICATIONS (Neon Dashboard) */}
      <section className="py-24 px-6 bg-[#111] text-white border-y-2 border-black relative overflow-hidden">
        {/* CRT Grid Background */}
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(#0f0 1px, transparent 1px), linear-gradient(90deg, #0f0 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-3 gap-12">

            {/* Header Column */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="md:col-span-1"
            >
              <div className="sticky top-24">
                <div className="w-16 h-16 bg-[#A0E7E5] border-2 border-black flex items-center justify-center rounded-lg mb-8 shadow-[4px_4px_0px_0px_#8C52FF]">
                  <Megaphone size={32} className="text-black" />
                </div>
                <h2 className="text-5xl md:text-7xl font-black mb-6 leading-none tracking-tighter text-white drop-shadow-[2px_2px_0px_#8C52FF]">
                  LOUD & <br /> <span className="text-[#A0E7E5]">CLEAR.</span>
                </h2>
                <p className="text-gray-400 text-lg font-medium leading-relaxed mb-8 border-l-4 border-[#A0E7E5] pl-6">
                  Strategic messaging, high-end production, and tech-driven marketing that cuts through the noise.
                </p>
              </div>
            </motion.div>

            {/* Content Grid (2 Columns) */}
            <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
              {commsServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, backgroundColor: "#222" }}
                  className="flex items-start gap-4 p-5 rounded-xl border-2 border-[#333] bg-[#222] hover:border-[#A0E7E5] hover:shadow-[4px_4px_0px_0px_#A0E7E5] transition-all group"
                >
                  <div className="text-[#666] group-hover:text-[#A0E7E5] transition-colors">
                    {React.cloneElement(service.icon, { size: 28 })}
                  </div>
                  <div>
                    <h3 className="text-lg font-black uppercase text-white mb-1">{service.title}</h3>
                    <p className="text-sm text-gray-400 leading-snug group-hover:text-gray-200">
                      {service.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section id="about" className="py-24 bg-[#FFD233] border-y-2 border-black px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="inline-block bg-black text-white px-4 py-2 rounded-lg font-bold mb-6 transform -rotate-2">
                ⚡ The NXTO Difference
              </div>
              <h2 className="text-4xl md:text-5xl font-black mb-6 leading-tight">WE DON'T DO "GOOD ENOUGH".</h2>
              <p className="text-lg md:text-xl font-medium mb-8 text-black/80">
                Most agencies give you a template and a bill. We give you a partnership. We treat your budget like it's our own money and your deadline like it's a bomb timer.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Innovation First', desc: 'We use the latest tech stack, not code from 2015.' },
                  { title: 'Strategic Growth', desc: 'Design that looks good? Basic. Design that sells? That\'s us.' },
                  { title: 'Total Transparency', desc: 'No hidden fees. No "mystery" delays. Just work.' }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ x: -20, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.2 }}
                    className="bg-white border-2 border-black p-4 rounded-xl shadow-[4px_4px_0px_0px_black] flex items-start gap-4"
                  >
                    <div className="bg-[#A0E7E5] p-2 rounded-lg border-2 border-black h-fit">
                      <CheckCircle size={20} className="text-black" />
                    </div>
                    <div>
                      <h4 className="font-black text-lg">{item.title}</h4>
                      <p className="text-sm text-gray-600 font-medium">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <div className="relative">
              <motion.div
                initial={{ rotate: 0 }}
                whileInView={{ rotate: 2 }}
                className="absolute inset-0 bg-black rounded-xl transform translate-x-4 translate-y-4"
              ></motion.div>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                whileHover={{ x: -5, y: -5 }}
                className="relative bg-white border-2 border-black rounded-xl p-8 overflow-hidden"
              >
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <Layers size={200} />
                </div>
                <h3 className="text-3xl font-black mb-6">THE STATS</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-[#F0F4F8] p-4 rounded-lg border-2 border-black text-center">
                    <div className="text-4xl font-black text-[#8C52FF]">98%</div>
                    <div className="text-xs font-bold uppercase mt-1">Client Retention</div>
                  </div>
                  <div className="bg-[#F0F4F8] p-4 rounded-lg border-2 border-black text-center">
                    <div className="text-4xl font-black text-[#FF6B6B]">300+</div>
                    <div className="text-xs font-bold uppercase mt-1">Projects Launched</div>
                  </div>
                  <div className="bg-[#F0F4F8] p-4 rounded-lg border-2 border-black text-center">
                    <div className="text-4xl font-black text-[#FFD233]">50M+</div>
                    <div className="text-xs font-bold uppercase mt-1">Users Impacted</div>
                  </div>
                  <div className="bg-[#F0F4F8] p-4 rounded-lg border-2 border-black text-center">
                    <div className="text-4xl font-black text-black">24/7</div>
                    <div className="text-xs font-bold uppercase mt-1">Panic Support</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section id="contact" className="py-24 px-6 bg-[#A0E7E5] border-y-2 border-black relative">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white border-2 border-black rounded-xl p-8 md:p-12 shadow-[8px_8px_0px_0px_black]"
          >
            <div className="text-center mb-10">
              <h2 className="text-4xl md:text-5xl font-black mb-4 uppercase">LET’S BUILD YOUR NEXT GROWTH STORY</h2>
              <p className="text-lg text-gray-600 font-medium">Tell us about your vision. We'll handle the rest.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-black text-sm uppercase mb-2">Name</label>
                  <input type="text" name="name" placeholder="Elon Musk" className="w-full bg-[#F0F4F8] border-2 border-black rounded-lg p-4 font-bold focus:outline-none focus:shadow-[4px_4px_0px_0px_#8C52FF] transition-all" required />
                </div>
                <div>
                  <label className="block font-black text-sm uppercase mb-2">Email</label>
                  <input type="email" name="email" placeholder="elon@mars.com" className="w-full bg-[#F0F4F8] border-2 border-black rounded-lg p-4 font-bold focus:outline-none focus:shadow-[4px_4px_0px_0px_#8C52FF] transition-all" required />
                </div>
              </div>

              <div>
                <label className="block font-black text-sm uppercase mb-2">Service Needed</label>
                <select name="service" className="w-full bg-[#F0F4F8] border-2 border-black rounded-lg p-4 font-bold focus:outline-none focus:shadow-[4px_4px_0px_0px_#8C52FF] transition-all appearance-none cursor-pointer">
                  <option>Web Development</option>
                  <option>App Development</option>
                  <option>Events & Experiential</option>
                  <option>Brand Activations</option>
                  <option>Digital Marketing</option>
                  <option>Brand Strategy</option>
                  <option>Just want to say hi 👋</option>
                </select>
              </div>

              <div>
                <label className="block font-black text-sm uppercase mb-2">Message</label>
                <textarea name="message" rows="4" placeholder="Tell us about your project..." className="w-full bg-[#F0F4F8] border-2 border-black rounded-lg p-4 font-bold focus:outline-none focus:shadow-[4px_4px_0px_0px_#8C52FF] transition-all" required></textarea>
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02, boxShadow: "6px 6px 0px 0px rgba(0,0,0,1)" }}
                whileTap={{ scale: 0.98 }}
                className={`w-full bg-[#FF6B6B] text-white py-4 rounded-lg font-black text-xl uppercase tracking-wider flex items-center justify-center gap-2 ${ hardShadow }`}
              >
                <Send size={24} /> Send It
              </motion.button>

              <div className="flex items-center justify-center gap-4 my-4">
                <span className="font-black text-sm uppercase">OR</span>
              </div>

              <motion.a
                whileHover={{ scale: 1.02, boxShadow: "6px 6px 0px 0px rgba(0,0,0,1)", backgroundColor: "#7a3ee0" }}
                whileTap={{ scale: 0.98 }}
                href="https://calendly.com/nxtomedia/30min"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full bg-[#8C52FF] text-white py-4 rounded-lg font-black text-xl uppercase tracking-wider flex items-center justify-center gap-2 ${ hardShadow }`}
              >
                <Calendar size={24} /> Schedule a Call
              </motion.a>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white pt-20 pb-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8 mb-12">
          <div className="flex flex-col items-center md:items-start">
            <div className="flex items-center gap-2 mb-2">
              <img src="/logo.png" alt="NXTO Logo" className="h-10 object-contain" />
            </div>
            <p className="font-bold text-gray-500">Next-Gen Digital Solutions.</p>
          </div>

          <div className="flex gap-8 font-bold text-sm">
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#8C52FF] hover:underline decoration-wavy">LINKEDIN</a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#8C52FF] hover:underline decoration-wavy">TWITTER</a>
            <a href="mailto:hello@nxtomedia.com" className="hover:text-[#8C52FF] hover:underline decoration-wavy">EMAIL</a>
          </div>
        </div>
        <div className="text-center font-bold text-gray-300 text-xs">
          © 2026 NXTO MEDIA. TRANSFORMING BRANDS DAILY.
        </div>
      </footer>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </Router>
  );
};

export default App;